(() => {
  'use strict';

  const releaseName = 'mn-storefront-release';
  const themeName = 'mn-storefront-theme';
  const revisionName = 'mn-collection-revision';
  const refreshParam = 'mn_refresh';
  const probeParam = 'mn_revalidate';
  const storageKey = 'mn-storefront-refresh:v1';
  const resumeAfter = 60000;
  const timeoutAfter = 5000;
  const readMeta = (doc, name) => {
    let value = doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content');
    if (!value && name === revisionName) value = doc.querySelector('[data-mn-collection-revision]')?.getAttribute('data-mn-collection-revision');
    return typeof value === 'string' && value.length <= 4096 ? value.trim() : '';
  };
  const markers = (doc) => ({
    release: readMeta(doc, releaseName),
    theme: readMeta(doc, themeName),
    revision: readMeta(doc, revisionName)
  });
  const initial = markers(document);
  if (!initial.release || !initial.theme || window.Shopify?.designMode) return;

  const incomingUrl = new URL(location.href);
  const incomingToken = incomingUrl.searchParams.get(refreshParam) || '';
  const cleanUrl = (value) => {
    const url = new URL(value);
    url.searchParams.delete(refreshParam);
    url.searchParams.delete(probeParam);
    return url;
  };
  if (incomingUrl.searchParams.has(refreshParam) || incomingUrl.searchParams.has(probeParam)) {
    const clean = cleanUrl(incomingUrl.href);
    try { history.replaceState(history.state, '', clean.href); } catch (_) {}
  }
  const protectedPath = (url) => /\/(?:cart|checkout|checkouts|account)(?:[/.]|$)/.test(url.pathname);
  if (protectedPath(incomingUrl)) return;

  let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : 0;
  let request = null;
  let serial = 0;
  let pending = null;
  let frame = 0;
  let navigating = false;
  let submitting = false;
  let lastCheckAt = 0;
  const touchedForms = new Set();
  const attempted = new Set(incomingToken ? [incomingToken] : []);

  const fingerprint = (value) => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
  };
  const routeFor = (value) => {
    const url = cleanUrl(value);
    url.hash = '';
    return url.href;
  };
  const isVisible = (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
  const isCartForm = (form) => {
    try { return /\/(?:cart|checkout|checkouts)(?:[/.]|$)/.test(new URL(form.action || '', location.href).pathname); } catch (_) { return false; }
  };
  const hasChangedField = (form) => Array.from(form.elements || []).some((field) => {
    if (field.disabled || !field.name) return false;
    const type = String(field.type || '').toLowerCase();
    if (['hidden', 'submit', 'button', 'reset', 'image'].includes(type)) return false;
    if (type === 'checkbox' || type === 'radio') return field.checked !== field.defaultChecked;
    if (field.tagName === 'SELECT') return Array.from(field.options).some(option => option.selected !== option.defaultSelected);
    return field.value !== field.defaultValue;
  });
  const isBusy = () => {
    if (submitting) return true;
    const active = document.activeElement;
    if (active && active.matches('input:not([type="hidden"]):not([disabled]):not([readonly]),textarea:not([disabled]):not([readonly]),select:not([disabled]),[contenteditable]:not([contenteditable="false"])')) return true;
    const overlays = document.querySelectorAll('.drawer[open],.modal[open],header-sidebar[open],cart-drawer[open],dialog[open],[role="dialog"][aria-modal="true"],.mn-newsletter-popup.is-open,.cloud-search-filters-pane');
    if (Array.from(overlays).some(isVisible)) return true;
    for (const form of touchedForms) {
      if (!form.isConnected || isCartForm(form)) touchedForms.delete(form);
      else if (hasChangedField(form)) return true;
      else touchedForms.delete(form);
    }
    return false;
  };
  const readAttempts = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value.filter(item => typeof item === 'string').slice(-16) : [];
    } catch (_) { return []; }
  };
  const markAttempt = (token) => {
    attempted.add(token);
    try {
      const values = readAttempts().filter(value => value !== token);
      values.push(token);
      sessionStorage.setItem(storageKey, JSON.stringify(values.slice(-16)));
    } catch (_) {}
  };
  const applyPending = () => {
    frame = 0;
    if (!pending || navigating || document.visibilityState === 'hidden' || navigator.onLine === false) return;
    if (routeFor(location.href) !== pending.route || readMeta(document, revisionName) !== pending.revision) { pending = null; return; }
    if (isBusy()) return;
    const token = pending.token;
    if (attempted.has(token) || readAttempts().includes(token)) { pending = null; return; }
    markAttempt(token);
    const destination = cleanUrl(location.href);
    destination.searchParams.set(refreshParam, token);
    navigating = true;
    pending = null;
    location.replace(destination.href);
  };
  const schedulePending = () => {
    if (pending && !frame) frame = requestAnimationFrame(applyPending);
  };

  const check = async () => {
    if (request || navigating || document.visibilityState === 'hidden' || navigator.onLine === false) return;
    const current = cleanUrl(location.href);
    if (protectedPath(current) || Date.now() - lastCheckAt < 1000) return;
    lastCheckAt = Date.now();
    const route = routeFor(current.href);
    const currentRevision = readMeta(document, revisionName);
    const probe = new URL(route);
    probe.searchParams.set(probeParam, `${Date.now().toString(36)}-${++serial}`);
    const controller = new AbortController();
    request = controller;
    const timeout = setTimeout(() => controller.abort(), timeoutAfter);
    try {
      const response = await fetch(probe.href, {
        method: 'GET', cache: 'no-store', credentials: 'same-origin',
        headers: { Accept: 'text/html' }, signal: controller.signal
      });
      if (!response.ok || !response.headers.get('Content-Type')?.toLowerCase().includes('text/html')) return;
      const responseUrl = new URL(response.url);
      if (responseUrl.origin !== current.origin || responseUrl.pathname !== current.pathname) return;
      const fresh = markers(new DOMParser().parseFromString(await response.text(), 'text/html'));
      if (controller.signal.aborted || routeFor(location.href) !== route || readMeta(document, revisionName) !== currentRevision || !fresh.release || !fresh.theme) return;
      const changed = fresh.release !== initial.release || fresh.theme !== initial.theme || Boolean(currentRevision && fresh.revision && currentRevision !== fresh.revision);
      if (!changed) { pending = null; return; }
      const token = 'v1-' + fingerprint([route, fresh.release, fresh.theme, fresh.revision].join('|'));
      pending = { route, token, revision: currentRevision };
      schedulePending();
    } catch (_) {
      // Offline sessions and unavailable checks keep their current usable page.
    } finally {
      clearTimeout(timeout);
      if (request === controller) request = null;
    }
  };

  document.addEventListener('input', (event) => {
    const form = event.target?.form;
    if (form && !isCartForm(form)) touchedForms.add(form);
  }, true);
  document.addEventListener('change', (event) => {
    const form = event.target?.form;
    if (form && !isCartForm(form)) touchedForms.add(form);
    schedulePending();
  }, true);
  document.addEventListener('submit', () => { submitting = true; }, true);
  document.addEventListener('reset', (event) => {
    touchedForms.delete(event.target);
    schedulePending();
  }, true);
  document.addEventListener('focusout', schedulePending, true);
  document.addEventListener('dialog:after-hide', schedulePending);
  window.addEventListener('pagehide', () => {
    request?.abort();
    request = null;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    submitting = false;
    navigating = false;
    lastCheckAt = 0;
    check();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    const elapsed = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    if (elapsed >= resumeAfter) check();
    else schedulePending();
  });
})();
